import { useState, useEffect, useRef, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listContactListsForOverage,
  getListOveragePlan,
  createListOverageJob,
  getListOverageJob,
  processListOverageJobChunk,
  cancelListOverageJob,
  type DidGroup,
  type ListOverageJob,
} from "../api/dids";

export function ListOveragePlannerPanel({
  groupId,
  groupName,
  groups = [],
  onSaved,
  onClose,
}: {
  groupId?: number | null;
  groupName?: string;
  groups?: DidGroup[];
  onSaved: () => void;
  onClose?: () => void;
}) {
  const qc = useQueryClient();
  const [selectedPoolId, setSelectedPoolId] = useState<number>(
    groupId || (groups[0]?.did_group_id ?? 0),
  );
  const effectivePoolId = groupId || selectedPoolId;
  const effectivePoolName =
    groupName ||
    groups.find((g) => g.did_group_id === effectivePoolId)?.name ||
    `Pool #${effectivePoolId}`;

  const [contactGroupId, setContactGroupId] = useState<number>(0);
  const [leadsPerDid, setLeadsPerDid] = useState<number>(100);
  const [overagePct, setOveragePct] = useState<number>(20);
  const [chunkSize, setChunkSize] = useState<number>(50);
  const [simulateProvision, setSimulateProvision] = useState<boolean>(false);

  // Per-NPA editable quantity overrides & selection
  const [customQtyByNpa, setCustomQtyByNpa] = useState<Record<string, number>>({});
  const [excludedNpas, setExcludedNpas] = useState<Set<string>>(new Set());

  // Active chunked AJAX job state
  const [activeJob, setActiveJob] = useState<ListOverageJob | null>(null);
  const [isRunningJob, setIsRunningJob] = useState<boolean>(false);
  const [pollTicks, setPollTicks] = useState<number>(0);
  const [jobError, setJobError] = useState<string | null>(null);
  const cancelRef = useRef<boolean>(false);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { data: contactLists = [], isLoading: listsLoading } = useQuery({
    queryKey: ["did-contact-lists", effectivePoolId],
    queryFn: () => listContactListsForOverage(effectivePoolId || undefined),
  });

  // Auto-select first contact list with contacts
  useEffect(() => {
    if (contactGroupId === 0 && contactLists.length > 0) {
      const firstWithContacts =
        contactLists.find((c) => c.contact_count > 0) ?? contactLists[0];
      if (firstWithContacts) {
        setContactGroupId(firstWithContacts.contact_group_id);
      }
    }
  }, [contactLists, contactGroupId]);

  const {
    data: plan,
    isLoading: planLoading,
    isError: planError,
    error: planErrObj,
    refetch: refetchPlan,
  } = useQuery({
    queryKey: [
      "list-overage-plan",
      effectivePoolId,
      contactGroupId,
      leadsPerDid,
      overagePct,
    ],
    queryFn: () =>
      getListOveragePlan({
        contact_group_id: contactGroupId,
        did_group_id: effectivePoolId,
        leads_per_did: Math.max(1, leadsPerDid || 100),
        overage_pct: Math.max(0, overagePct || 0),
      }),
    enabled: effectivePoolId > 0 && contactGroupId > 0,
  });

  // Reset overrides when plan inputs change
  useEffect(() => {
    setCustomQtyByNpa({});
    setExcludedNpas(new Set());
  }, [effectivePoolId, contactGroupId, leadsPerDid, overagePct]);

  // Cleanup poller on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, []);

  const effectiveRows = useMemo(() => {
    if (!plan) return [];
    return plan.rows.map((r) => {
      const qty =
        customQtyByNpa[r.npa] !== undefined ? customQtyByNpa[r.npa] : r.to_buy;
      const included = !excludedNpas.has(r.npa) && qty > 0;
      return {
        ...r,
        effectiveQty: qty,
        included,
        effectiveCost: Math.round(qty * plan.unit_rate * 100) / 100,
      };
    });
  }, [plan, customQtyByNpa, excludedNpas]);

  const selectedItems = useMemo(
    () =>
      effectiveRows
        .filter((r) => r.included && r.effectiveQty > 0)
        .map((r) => ({ npa: r.npa, qty: r.effectiveQty })),
    [effectiveRows],
  );

  const totalToBuy = useMemo(
    () => selectedItems.reduce((acc, it) => acc + it.qty, 0),
    [selectedItems],
  );

  const totalEstCost = useMemo(
    () => Math.round(totalToBuy * (plan?.unit_rate ?? 1.5) * 100) / 100,
    [totalToBuy, plan?.unit_rate],
  );

  const estimatedChunks = useMemo(() => {
    if (totalToBuy <= 0) return 0;
    const effChunk = Math.max(1, Math.min(chunkSize || 50, 100));
    return Math.ceil(totalToBuy / effChunk);
  }, [totalToBuy, chunkSize]);

  async function startChunkedPurchase() {
    if (!effectivePoolId || selectedItems.length === 0) return;
    setJobError(null);
    cancelRef.current = false;
    setIsRunningJob(true);
    setPollTicks(0);

    try {
      const effChunkSize =
        totalToBuy > 100 ? Math.min(chunkSize, 100) : Math.min(chunkSize, 100);
      const created = await createListOverageJob({
        did_group_id: effectivePoolId,
        contact_group_id: contactGroupId || undefined,
        leads_per_did: leadsPerDid,
        overage_pct: overagePct,
        chunk_size: effChunkSize,
        items: selectedItems,
        simulate_provision: simulateProvision,
      });
      setActiveJob(created);

      // Start AJAX status polling interval while chunks are processed
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollTimerRef.current = setInterval(async () => {
        try {
          const polled = await getListOverageJob(created.job_id);
          setActiveJob(polled);
          setPollTicks((t) => t + 1);
        } catch {
          // ignore transient poll blips
        }
      }, 450);

      // Sequential AJAX chunk driver loop
      let current = created;
      while (
        !cancelRef.current &&
        current.status !== "completed" &&
        current.status !== "cancelled" &&
        current.status !== "failed"
      ) {
        current = await processListOverageJobChunk(created.job_id);
        setActiveJob(current);
        setPollTicks((t) => t + 1);
      }

      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }

      // Final status fetch
      const finalJob = await getListOverageJob(created.job_id);
      setActiveJob(finalJob);

      qc.invalidateQueries({ queryKey: ["did-group"] });
      qc.invalidateQueries({ queryKey: ["did-groups"] });
      qc.invalidateQueries({ queryKey: ["dids"] });
      refetchPlan();
      onSaved();
    } catch (err) {
      setJobError((err as Error).message || "Failed to execute chunked purchase");
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    } finally {
      setIsRunningJob(false);
    }
  }

  async function handleCancelJob() {
    if (!activeJob) return;
    cancelRef.current = true;
    try {
      const updated = await cancelListOverageJob(activeJob.job_id);
      setActiveJob(updated);
    } catch (err) {
      setJobError((err as Error).message);
    }
  }

  return (
    <div className="space-y-4" data-testid="list-overage-planner-panel">
      {/* Pool + Contact List + Sizing Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-gray-50 p-3.5 rounded-lg border border-gray-200">
        {!groupId && (
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Target DID Pool
            </label>
            <select
              value={String(effectivePoolId)}
              onChange={(e) => setSelectedPoolId(Number(e.target.value))}
              data-testid="list-overage-pool-select"
              className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm bg-white"
            >
              <option value="0">-- Select a DID Pool --</option>
              {groups.map((g) => (
                <option key={g.did_group_id} value={String(g.did_group_id)}>
                  {g.name} ({g.member_count ?? 0} DIDs)
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            1. Select Contact List to Calculate Area Code (NPA) Overage
          </label>
          <select
            value={String(contactGroupId)}
            onChange={(e) => setContactGroupId(Number(e.target.value))}
            disabled={listsLoading || isRunningJob}
            data-testid="list-overage-contact-select"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white font-medium"
          >
            <option value="0">
              {listsLoading ? "Loading contact lists…" : "-- Choose a Contact List --"}
            </option>
            {contactLists.map((cl) => (
              <option key={cl.contact_group_id} value={String(cl.contact_group_id)}>
                {cl.name} — {cl.contact_count.toLocaleString()} contacts
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            2. Target Leads per DID Ratio
          </label>
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min={1}
              max={10000}
              value={leadsPerDid}
              disabled={isRunningJob}
              onChange={(e) => setLeadsPerDid(Math.max(1, Number(e.target.value) || 1))}
              data-testid="list-overage-leads-per-did-input"
              className="w-28 border border-gray-300 rounded px-2.5 py-1.5 text-sm font-mono bg-white"
            />
            <div className="flex gap-1 text-xs">
              {[1, 10, 25, 50, 100].map((preset) => (
                <button
                  key={preset}
                  type="button"
                  disabled={isRunningJob}
                  onClick={() => setLeadsPerDid(preset)}
                  data-testid={`leads-per-did-preset-${preset}`}
                  className={`px-2 py-1 rounded border ${
                    leadsPerDid === preset
                      ? "bg-brand text-white border-brand"
                      : "bg-white text-gray-600 border-gray-200 hover:bg-gray-100"
                  }`}
                >
                  {preset}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            3. Overage Headroom Buffer (%)
          </label>
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min={0}
              max={500}
              value={overagePct}
              disabled={isRunningJob}
              onChange={(e) => setOveragePct(Math.max(0, Number(e.target.value) || 0))}
              data-testid="list-overage-pct-input"
              className="w-20 border border-gray-300 rounded px-2.5 py-1.5 text-sm font-mono bg-white"
            />
            <div className="flex gap-1 text-xs">
              {[0, 20, 50, 100].map((pct) => (
                <button
                  key={pct}
                  type="button"
                  disabled={isRunningJob}
                  onClick={() => setOveragePct(pct)}
                  data-testid={`overage-pct-preset-${pct}`}
                  className={`px-2 py-1 rounded border ${
                    overagePct === pct
                      ? "bg-brand text-white border-brand"
                      : "bg-white text-gray-600 border-gray-200 hover:bg-gray-100"
                  }`}
                >
                  +{pct}%
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 pt-1 md:col-span-2 border-t border-gray-200 mt-1">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-600 font-medium">AJAX Chunk Size:</span>
            <select
              value={String(chunkSize)}
              disabled={isRunningJob}
              onChange={(e) => setChunkSize(Number(e.target.value))}
              data-testid="list-overage-chunk-size-select"
              className="border border-gray-300 rounded px-2 py-1 text-xs bg-white"
            >
              <option value="25">25 numbers / chunk</option>
              <option value="50">50 numbers / chunk (Recommended &gt;100)</option>
              <option value="100">100 numbers / chunk (Max per batch)</option>
            </select>
          </div>

          <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={simulateProvision}
              disabled={isRunningJob}
              onChange={(e) => setSimulateProvision(e.target.checked)}
              data-testid="list-overage-simulate-checkbox"
            />
            <span>Simulate / Test Provision into Pool (No Carrier Charge)</span>
          </label>
        </div>
      </div>

      {/* Plan Summary & Area Code Breakdown */}
      {planLoading && (
        <div className="p-4 text-center text-sm text-gray-500 bg-gray-50 rounded border border-gray-200">
          Analyzing contact list area codes against pool "{effectivePoolName}"…
        </div>
      )}

      {planError && (
        <div className="p-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded">
          {(planErrObj as Error)?.message || "Failed to compute overage plan"}
        </div>
      )}

      {plan && (
        <div className="space-y-3">
          {/* Summary KPI Strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div className="p-2.5 rounded bg-gray-50 border border-gray-200">
              <div className="text-gray-500">Contacts in List</div>
              <div
                className="text-base font-bold text-gray-900"
                data-testid="overage-total-contacts"
              >
                {plan.total_contacts.toLocaleString()}
              </div>
              <div className="text-[11px] text-gray-400 truncate">
                {plan.contact_group_name}
              </div>
            </div>
            <div className="p-2.5 rounded bg-gray-50 border border-gray-200">
              <div className="text-gray-500">Pool Active DIDs</div>
              <div
                className="text-base font-bold text-green-700"
                data-testid="overage-existing-dids"
              >
                {plan.existing_active_dids}
              </div>
              <div className="text-[11px] text-gray-400">
                {plan.existing_matching_dids} in list NPAs
              </div>
            </div>
            <div className="p-2.5 rounded bg-gray-50 border border-gray-200">
              <div className="text-gray-500">Target w/ +{overagePct}%</div>
              <div
                className="text-base font-bold text-indigo-700"
                data-testid="overage-target-dids"
              >
                {plan.target_total_dids}
              </div>
              <div className="text-[11px] text-gray-400">
                @ {leadsPerDid} leads/DID
              </div>
            </div>
            <div className="p-2.5 rounded bg-amber-50 border border-amber-200">
              <div className="text-amber-800 font-medium">Overage to Buy</div>
              <div
                className="text-base font-bold text-amber-900"
                data-testid="overage-total-to-buy"
              >
                {totalToBuy} DIDs
              </div>
              <div className="text-[11px] text-amber-700">
                Est. ${totalEstCost.toFixed(2)}/{plan.billing_unit}
              </div>
            </div>
          </div>

          {/* >100 Chunked AJAX Banner */}
          {totalToBuy > 100 && (
            <div
              className="px-3 py-2 rounded bg-indigo-50 border border-indigo-200 text-xs text-indigo-900 flex items-center justify-between gap-2"
              data-testid="overage-chunking-banner"
            >
              <div>
                <strong>⚡ Chunked AJAX Mode Enabled (&gt;100 Numbers):</strong>{" "}
                Your order of <strong>{totalToBuy} numbers</strong> will be automatically split into{" "}
                <strong>{estimatedChunks} chunks</strong> of up to{" "}
                <strong>{chunkSize} numbers</strong> each and polled live.
              </div>
              <span className="px-2 py-0.5 rounded bg-indigo-600 text-white font-mono text-[11px] shrink-0">
                {estimatedChunks} AJAX Chunks
              </span>
            </div>
          )}

          {/* Live Chunked AJAX Execution & Polling Progress Panel */}
          {activeJob && (
            <div
              className="p-4 rounded-lg border-2 border-indigo-300 bg-indigo-50/60 space-y-3"
              data-testid="list-overage-job-panel"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-indigo-950">
                    Chunked AJAX Purchase Runner
                  </span>
                  <span
                    data-testid="list-overage-job-status"
                    className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${
                      activeJob.status === "completed"
                        ? "bg-green-100 text-green-800"
                        : activeJob.status === "running" || activeJob.status === "pending"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-amber-100 text-amber-800"
                    }`}
                  >
                    {activeJob.status}
                  </span>
                  <span
                    className="text-[11px] text-gray-500 font-mono"
                    data-testid="list-overage-poll-count"
                  >
                    Polls: {pollTicks}
                  </span>
                </div>

                {(activeJob.status === "running" || activeJob.status === "pending") && (
                  <button
                    type="button"
                    onClick={handleCancelJob}
                    data-testid="cancel-list-overage-job-btn"
                    className="px-2.5 py-1 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-medium"
                  >
                    Stop After Current Chunk
                  </button>
                )}
              </div>

              <div className="text-xs text-indigo-900 flex flex-wrap items-center justify-between gap-2">
                <span data-testid="list-overage-progress-text">
                  Chunk <strong>{activeJob.chunks_completed}</strong> of{" "}
                  <strong>{activeJob.chunks_total}</strong> ·{" "}
                  <strong>{activeJob.total_processed}</strong> /{" "}
                  <strong>{activeJob.total_requested}</strong> processed ·{" "}
                  <strong data-testid="list-overage-bought-count">
                    {activeJob.total_bought}
                  </strong>{" "}
                  provisioned into "{activeJob.did_group_name}"
                </span>
                <span className="font-mono font-bold" data-testid="list-overage-progress-pct">
                  {activeJob.progress_pct}%
                </span>
              </div>

              <div className="w-full bg-indigo-100 rounded-full h-3 overflow-hidden">
                <div
                  data-testid="list-overage-progress-bar"
                  className={`h-3 transition-all duration-300 ${
                    activeJob.status === "completed"
                      ? "bg-green-600"
                      : activeJob.status === "cancelled" || activeJob.status === "failed"
                      ? "bg-amber-500"
                      : "bg-indigo-600"
                  }`}
                  style={{ width: `${activeJob.progress_pct}%` }}
                />
              </div>

              {/* Per-Chunk Table */}
              <div className="max-h-40 overflow-y-auto border border-indigo-200 rounded bg-white">
                <table
                  className="w-full text-xs"
                  data-testid="list-overage-chunks-table"
                >
                  <thead className="bg-indigo-50 text-indigo-900 sticky top-0">
                    <tr>
                      <th className="px-2.5 py-1.5 text-left">Chunk</th>
                      <th className="px-2.5 py-1.5 text-left">Area Codes (NPA)</th>
                      <th className="px-2.5 py-1.5 text-right">Batch Qty</th>
                      <th className="px-2.5 py-1.5 text-right">Bought</th>
                      <th className="px-2.5 py-1.5 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {activeJob.chunks.map((ch) => (
                      <tr
                        key={ch.chunk_index}
                        data-testid={`list-overage-chunk-row-${ch.chunk_index}`}
                      >
                        <td className="px-2.5 py-1.5 font-mono font-medium">
                          #{ch.chunk_index}
                        </td>
                        <td className="px-2.5 py-1.5 font-mono text-gray-700">
                          {ch.npa_summary}
                        </td>
                        <td className="px-2.5 py-1.5 text-right font-mono">{ch.qty}</td>
                        <td className="px-2.5 py-1.5 text-right font-mono font-semibold text-green-700">
                          {ch.bought}
                        </td>
                        <td className="px-2.5 py-1.5">
                          <span
                            data-testid={`list-overage-chunk-status-${ch.chunk_index}`}
                            className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${
                              ch.status === "completed"
                                ? "bg-green-100 text-green-800"
                                : ch.status === "running"
                                ? "bg-blue-100 text-blue-800"
                                : ch.status === "pending"
                                ? "bg-gray-100 text-gray-600"
                                : "bg-amber-100 text-amber-800"
                            }`}
                          >
                            {ch.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {jobError && (
            <p className="text-xs text-red-600 font-medium" data-testid="list-overage-error">
              {jobError}
            </p>
          )}

          {/* Per-Area-Code (NPA) Overage Breakdown Table */}
          <div className="max-h-60 overflow-y-auto border border-gray-200 rounded">
            <table className="w-full text-xs" data-testid="list-overage-npa-table">
              <thead className="bg-gray-50 text-gray-600 sticky top-0">
                <tr>
                  <th className="px-2.5 py-2 w-7 text-left">
                    <input
                      type="checkbox"
                      checked={
                        effectiveRows.length > 0 &&
                        effectiveRows.every((r) => !excludedNpas.has(r.npa))
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setExcludedNpas(new Set());
                        } else {
                          setExcludedNpas(new Set(effectiveRows.map((r) => r.npa)));
                        }
                      }}
                      data-testid="list-overage-select-all-npas"
                    />
                  </th>
                  <th className="px-2.5 py-2 text-left">Area Code</th>
                  <th className="px-2.5 py-2 text-left">State</th>
                  <th className="px-2.5 py-2 text-right">Contacts</th>
                  <th className="px-2.5 py-2 text-right">Active in Pool</th>
                  <th className="px-2.5 py-2 text-right">Target (+{overagePct}%)</th>
                  <th className="px-2.5 py-2 text-right">Overage to Buy</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {effectiveRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-4 text-center text-gray-500">
                      No valid US area codes found in this contact list.
                    </td>
                  </tr>
                ) : (
                  effectiveRows.map((r) => (
                    <tr
                      key={r.npa}
                      className="hover:bg-gray-50"
                      data-testid={`list-overage-npa-row-${r.npa}`}
                    >
                      <td className="px-2.5 py-1.5">
                        <input
                          type="checkbox"
                          checked={!excludedNpas.has(r.npa)}
                          disabled={isRunningJob}
                          onChange={() => {
                            setExcludedNpas((prev) => {
                              const next = new Set(prev);
                              if (next.has(r.npa)) next.delete(r.npa);
                              else next.add(r.npa);
                              return next;
                            });
                          }}
                          data-testid={`list-overage-npa-check-${r.npa}`}
                        />
                      </td>
                      <td className="px-2.5 py-1.5 font-mono font-bold text-gray-900">
                        {r.npa}
                      </td>
                      <td className="px-2.5 py-1.5 text-gray-600">{r.state}</td>
                      <td className="px-2.5 py-1.5 text-right font-mono">
                        {r.contacts.toLocaleString()}
                      </td>
                      <td className="px-2.5 py-1.5 text-right font-mono text-green-700">
                        {r.active_dids}
                      </td>
                      <td className="px-2.5 py-1.5 text-right font-mono text-indigo-700">
                        {r.target_dids}
                      </td>
                      <td className="px-2.5 py-1.5 text-right">
                        <input
                          type="number"
                          min={0}
                          max={1000}
                          disabled={isRunningJob}
                          value={r.effectiveQty}
                          onChange={(e) => {
                            const val = Math.max(0, Number(e.target.value) || 0);
                            setCustomQtyByNpa((prev) => ({
                              ...prev,
                              [r.npa]: val,
                            }));
                          }}
                          data-testid={`list-overage-qty-${r.npa}`}
                          className="w-20 border border-gray-300 rounded px-2 py-0.5 text-right font-mono text-xs"
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Action Footer */}
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-200">
            <div className="text-xs text-gray-600">
              {totalToBuy > 100 ? (
                <span>
                  Will execute <strong>{estimatedChunks} AJAX chunks</strong> of{" "}
                  <strong>≤{chunkSize} numbers</strong> into{" "}
                  <strong>{effectivePoolName}</strong>
                </span>
              ) : (
                <span>
                  Buying <strong>{totalToBuy}</strong> overage DID(s) into{" "}
                  <strong>{effectivePoolName}</strong>
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isRunningJob}
                  className="px-3 py-2 rounded bg-gray-100 hover:bg-gray-200 text-xs font-medium disabled:opacity-50"
                >
                  Close
                </button>
              )}
              <button
                type="button"
                disabled={
                  !effectivePoolId ||
                  totalToBuy <= 0 ||
                  isRunningJob
                }
                onClick={startChunkedPurchase}
                data-testid="start-list-overage-buy-btn"
                className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-50"
              >
                {isRunningJob
                  ? `Processing Chunk ${activeJob?.current_chunk_index ?? 1}/${
                      activeJob?.chunks_total ?? estimatedChunks
                    }…`
                  : totalToBuy > 100
                  ? `Buy ${totalToBuy} Overage DIDs (${estimatedChunks} AJAX Chunks)`
                  : `Buy ${totalToBuy} Overage DIDs`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
